// Task history tab — background task list, auto-refreshing every 5s.
// Shows progress, phase, evidence, duration, error, cancel button.

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Empty } from "@/components/ui/Empty";
import { Loading } from "@/components/ui/Loading";
import {
  useCancelTask,
  useEngagementTasks,
} from "@/hooks/useEngagementDetail";
import { confirm } from "@/stores/confirmStore";
import { toast } from "@/stores/toastStore";
import { useDict, fmt } from "@/i18n/LocaleProvider";

const STATUS_COLORS: Record<string, string> = {
  queued: "rgb(var(--color-warning))",
  running: "rgb(var(--color-accent))",
  completed: "rgb(var(--color-success))",
  failed: "rgb(var(--color-danger))",
  cancelled: "rgb(var(--color-muted))",
  retrying: "rgb(var(--color-brand))",
  timeout: "rgb(var(--color-warning))",
};

const PRIORITY_COLORS: Record<string, string> = {
  high: "rgb(var(--color-danger))",
  normal: "rgb(var(--color-accent))",
  low: "rgb(var(--color-muted))",
};

export function TaskHistoryTab({ engagementId }: { engagementId: string }) {
  const th = useDict().app.taskHistory;
  const { data, isLoading, isError, error } = useEngagementTasks(engagementId);
  const cancel = useCancelTask(engagementId);

  if (isError) {
    return (
      <Card className="border-danger">
        <div className="text-danger font-mono text-[12px]">
          {th.error} {error instanceof Error ? error.message : String(error)}
        </div>
      </Card>
    );
  }

  if (isLoading || !data) return <Loading text={th.loading} />;

  if (data.length === 0) {
    return (
      <Empty text={th.none} />
    );
  }

  async function cancelTask(tid: string) {
    const ok = await confirm({
      title: th.cancelTitle,
      message:
        th.cancelMsg,
      variant: "danger",
      confirmLabel: th.cancelConfirm,
      cancelLabel: th.cancelKeep,
    });
    if (!ok) return;
    try {
      await cancel.mutateAsync(tid);
      toast.success(th.cancelled);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="font-mono text-[11px] text-muted mb-2">
        {fmt(th.count, { n: data.length })}
      </div>
      {data.map((t) => {
        const isActive = t.status === "running" || t.status === "queued";
        const dur = t.duration_seconds
          ? `${Math.floor(t.duration_seconds)}s`
          : t.started_at
            ? th.running
            : "-";
        return (
          <Card
            key={t.id}
            style={{ borderColor: isActive ? "rgb(var(--color-accent))" : undefined }}
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="font-display text-[15px] font-semibold text-content">
                {th.taskLabel} #{t.display_number ?? "—"}
              </div>
              <Badge color={STATUS_COLORS[t.status] ?? "rgb(var(--color-muted))"} bg="rgb(var(--color-bg3))">
                {th.statuses[t.status as keyof typeof th.statuses] ?? t.status}
              </Badge>
              <Badge
                color={PRIORITY_COLORS[t.priority] ?? "rgb(var(--color-muted))"}
                bg="rgb(var(--color-bg3))"
              >
                {th.priorities[t.priority as keyof typeof th.priorities] ?? t.priority} {th.prioritySuffix}
              </Badge>
              {t.retry_count && t.retry_count > 0 && (
                <Badge color="rgb(var(--color-warning))" bg="rgb(var(--color-warning-bg))">
                  {fmt(th.retry, { n: t.retry_count })}
                </Badge>
              )}
              <div className="flex-1" />
              {isActive && (
                <Button
                  small
                  variant="danger"
                  onClick={() => cancelTask(t.id)}
                >
                  {th.cancelBtn}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
              <div>
                <div className="font-mono text-[9px] text-muted">{th.progress}</div>
                <div className="font-display text-[16px] text-accent font-semibold">
                  {t.progress_pct ?? 0}%
                </div>
              </div>
              <div>
                <div className="font-mono text-[9px] text-muted">{th.phase}</div>
                <div className="font-mono text-[11px] text-content">
                  {t.current_phase ?? "-"}
                </div>
              </div>
              <div>
                <div className="font-mono text-[9px] text-muted">{th.evidence}</div>
                <div className="font-mono text-[11px] text-content">
                  {t.evidence_count ?? 0}
                </div>
              </div>
              <div>
                <div className="font-mono text-[9px] text-muted">{th.duration}</div>
                <div className="font-mono text-[11px] text-content">{dur}</div>
              </div>
            </div>
            {isActive && (
              <div className="h-1 bg-bg2 rounded-[2px] overflow-hidden mb-2">
                <div
                  className="h-full bg-accent transition-[width] duration-500"
                  style={{ width: `${t.progress_pct ?? 0}%` }}
                />
              </div>
            )}
            <div className="flex flex-wrap gap-x-3 gap-y-1 justify-between font-mono text-[10px] text-dim">
              <span>
                {th.created}{" "}
                {t.created_at ? new Date(t.created_at).toLocaleString() : "-"}
              </span>
              <span>
                {t.completed_at
                  ? th.done + " " + new Date(t.completed_at).toLocaleString()
                  : ""}
              </span>
            </div>
            {t.error_message && (
              <div
                className="mt-2 p-2 rounded font-mono text-[10px] text-danger"
                style={{
                  background: "rgb(var(--color-danger-bg))",
                  border: "1px solid rgb(var(--color-danger) / 0.251)",
                  maxHeight: "100px",
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                {t.error_message.substring(0, 500)}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
